import { z } from 'zod';
/**
 * The domain enums from plan §4, shared by every area's schemas. They are persisted as `varchar` +
 * `$type<>()` (never pg enums), so the service-side unions in `src/app/domain` mirror these exactly.
 */
export const powertrainTypeSchema = z.enum(['ICE', 'HYBRID', 'PHEV', 'BEV', 'DIESEL']);
export const seasonSchema = z.enum(['SUMMER', 'WINTER']);
export const timeBandSchema = z.enum(['DAY', 'NIGHT']);
export const hvacStateSchema = z.enum(['AC_ON', 'AC_OFF']);
export const routeTypeSchema = z.enum(['URBAN', 'HIGHWAY', 'MIXED', 'GRADE']);
export const testTypeSchema = z.enum(['DRIVE', 'IDLE']);
export const publishedRegimeSchema = z.enum(['WLTP', 'EPA', 'GCC', 'OTHER']);
/**
 * How busy the road is — read on two layers, never on the route (the same circuit is a different test at
 * 08:00 and at 14:00, so a route cannot own a traffic level):
 *
 * - on a **scenario** it is the *expected* traffic, one dimension of the standardized tuple two runs have
 *   to share to be comparable at all, and `null` when the scenario does not prescribe one;
 * - on a **run** it is the *actual* traffic the tester met, recorded beside the temperature and the
 *   payload, judged against the scenario's expectation by the `traffic_matches_scenario` validity rule and
 *   across compared runs by the `traffic_match` compatibility check.
 */
export const trafficLevelSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
/** Status of the catalogue rows a manager can switch on and off (segments, protocols, scenarios). */
export const catalogueStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
/** Vehicles are never deleted — they are archived, so historical runs keep resolving. */
export const vehicleStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
/** A route revision is superseded by its successor rather than edited (plan §4). */
export const routeStatusSchema = z.enum(['ACTIVE', 'SUPERSEDED']);
/** The run lifecycle from plan §3.2: a draft is filled in, submitted, then judged by the validity engine. */
export const runStatusSchema = z.enum(['DRAFT', 'SUBMITTED', 'VALID', 'VALID_WITH_WARNING', 'INVALID']);
/** How a run entered the system — the wizard or the spreadsheet import (Phase 3). */
export const runSourceSchema = z.enum(['MANUAL', 'IMPORT']);
/** What a broken validity rule costs a run. Temperature rules seed as WARN only (plan §12.3). */
export const ruleSeveritySchema = z.enum(['FAIL', 'WARN']);
/**
 * The per-rule verdict recorded on a run. A rule that does not apply to the run at all records nothing;
 * `SKIPPED` is the narrower case of a rule that *does* apply but has no reference to measure against —
 * a distance rule on a route with no surveyed distance — and it never counts towards the run's status.
 */
export const validationOutcomeSchema = z.enum(['PASS', 'WARN', 'FAIL', 'SKIPPED']);
//# sourceMappingURL=enum.schemas.js.map